import React, { useState, useContext, useRef } from 'react';
import { ModuleBase, UmbracoForm, Link, SmootherContext } from 'components';
import { fixColor, isColorDark } from '@/utils';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { selectSiteTheme } from '../store/index';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

const FormModule = ({ data, isPopup }) => {
  const { t } = useTranslation('common');
  const [headingData, setHeadingData] = useState({
    preHeading: data.preHeading,
    heading: data.heading,
    errorHeading: data.errorHeading,
    text: data.text,
    cta: null,
  });
  const [preferedMethod, setPreferedMethod] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const router = useRouter();
  const scrollSmoother = useContext(SmootherContext);
  const boxRef = useRef();
  const isCajaLanding = router.asPath.includes('caja-by-maxx-royal/caja-landing');
  const siteTheme = useSelector(selectSiteTheme);

  const onFinish = () => {
    setTimeout(() => {
      scrollSmoother.scrollTo(boxRef.current, true, 'top 100px');
    }, 100);
  };

  const formDefinition = JSON.parse(JSON.stringify(data.formFields));
  formDefinition.pages?.forEach((page, i) => {
    page?.fieldsets.forEach((fieldset, j) => {
      fieldset?.columns.forEach((column, k) => {
        column?.fields.forEach((field) => {
          if (['chooseAHotel', 'resortOfInterest'].includes(field.alias)) {
            field.preValues = data.hotels.map((hotel) => ({ caption: hotel.name, value: hotel.name }));
          } else if (field.alias === 'royaltyCardLevel') {
            field.preValues = data.royaltyCardTypes.map((cardType) => ({
              caption: cardType.value,
              value: cardType.value,
            }));
          } else if (field.alias === 'enquiryType') {
            field.preValues = data.requestReasons.map((reason) => ({
              caption: reason.value,
              value: reason.value,
            }));
          } else if (field.alias === 'preferredTimeOfCall') {
            field.preValues = data.callTimes.map((callTime) => ({
              caption: callTime.value,
              value: callTime.value,
            }));
          } else if (field.alias === 'language' && data.formFields.name === 'Newsletter') {
            field.preValues = data.languages.map((lang) => ({
              caption: lang,
              value: lang,
            }));
            field.settings.defaultValue = data.languages[0];
          } else if (field.alias === 'dateOfBirth' && field.type.name === 'Date') {
            field.settings.maxDate = new Date();
            field.settings.yearMonthSelector = true;
          } else if (formDefinition?.cssClass === 'contact-us' && field.type.name === 'Data Consent') {
            field.type = { ...field.type, name: 'scrollableCheckbox' };
          }
        });

        if (
          i === data.formFields.pages.length - 1 &&
          j === page?.fieldsets.length - 1 &&
          k === fieldset?.columns.length - 1
        ) {
          column?.fields.push({
            alias: 'recaptcha',
            caption: 'recaptcha',
            helpText: '',
            id: '5d4b9338-6532-4316-8607-411a7317bea8',
            pattern: '',
            patternInvalidErrorMessage: 'Please provide a valid value for recaptcha',
            placeholder: '',
            preValues: [],
            required: true,
            requiredErrorMessage: 'Please provide a value for recaptcha',
            settings: {
              theme: '',
              size: '',
              errorMessage: '',
              showLabel: 'True',
            },
            type: {
              id: 'b69deaeb-ed75-4dc9-bfb8-d036bf9d3730',
              name: 'Recaptcha2',
              renderInputType: 'Single',
              supportsPreValues: false,
              supportsUploadTypes: false,
            },
          });
        }
      });
    });
  });

  let backgroundColour;

  if (isCajaLanding && isPopup) {
    backgroundColour = 'rgba(0,0,0,0.5)';
  } else if (!isCajaLanding && formDefinition?.cssClass === 'contact-us' && !isPopup && siteTheme !== 'Gold') {
    backgroundColour = '#C6CEBC';
  } else if (data.backgroundColour) {
    backgroundColour = data.backgroundColour;
  } else {
    backgroundColour = '#475A4F';
  }

  const dark = isColorDark(fixColor(backgroundColour));

  const cajaVillasHeadingData = {
    tr: {
      preHeading: 'Bu Ayrıcalıklı Dünyanın Kapılarını Birlikte Aralayalım!',
      heading: (
        <>
          <em>
            Caja by Maxx Royal’de sizi bekleyen bu benzersiz hayatı daha yakından keşfetmek ve size özel detayları
            planlamak için yanınızdayız. Aşağıdaki alana bilgilerinizi bırakın, sizinle iletişime geçelim. Pazarlama ve
            iletişim süreçleri FYP tarafından yürütülmektedir. FYP Aydınlatma Metni&apos;ne ulaşmak için
          </em>{' '}
          <a
            className="italic font-bold"
            rel="noopener"
            target="_blank"
            href="https://www.fyp.com.tr/kisisel-verilerin-islenmesine-iliskin-aydinlatma-ve-acik-riza-metni-2/"
          >
            tıklayınız
          </a>
        </>
      ),
    },
    'en-US': {
      preHeading: 'Begin Your Journey into a Distinctive World!',
      heading: (
        <>
          <em>
            We would be delighted to introduce you to the exceptional lifestyle awaiting you at Caja by Maxx Royal and
            to guide you through the details tailored to you. Simply leave your details below and our dedicated team
            will contact you shortly., marketing, and communication processes are carried out by FYP. Click here to
            access the FYP Clarification Text.
          </em>
        </>
      ),
    },
    de: {
      preHeading: 'Bleiben Sie über exklusive Vorteile informiert',
      heading: (
        <>
          <em>
            Verkauf, Marketing und Kommunikationsprozesse werden von FYP durchgeführt. Klicken Sie hier, um auf den FYP
            Aufklärungstext zuzugreifen.
          </em>
        </>
      ),
    },
    ru: {
      preHeading: 'Откройте мир исключительных привилегий с нами.',
      heading: (
        <>
          <em>
            Мы будем рады помочь вам ближе познакомиться с уникальным форматом жизни в Caja by Maxx Royal и продумать
            решения, которые подойдут именно вам. Заполните форму ниже, и мы свяжемся с вами в ближайшее время. ,
            маркетинг и коммуникационные процессы осуществляются компанией FYP. Нажмите здесь, чтобы ознакомиться с
            разъяснительным текстом FYP.
          </em>
        </>
      ),
    },
  };

  const whatsappConsentHeadingData = {
    tr: {
      preHeading: 'MRA Villa Satış WhatsApp Onay Formu',
      heading: t(`forms.whatsappconsentform.$whatsappconsentformKvkkConsentText`),
    },
    'en-US': {
      preHeading: 'MRA Villa Sales WhatsApp Consent Form',
      heading: t(`forms.whatsappconsentform.$whatsappconsentformKvkkConsentText`),
    },
    de: {
      preHeading: 'MRA Villa-Verkauf WhatsApp-Einwilligungsformular',
      heading: t(`forms.whatsappconsentform.$whatsappconsentformKvkkConsentText`),
    },
    ru: {
      preHeading: 'Форма согласия на использование WhatsApp для продаж вилл MRA',
      heading: t(`forms.whatsappconsentform.$whatsappconsentformKvkkConsentText`),
    },
  };

  return (
    <ModuleBase
      data={{ ...data, backgroundColour }}
      className={classNames('text-black font-didot', formDefinition?.cssClass)}
    >
      <div ref={boxRef} />
      {!submitted && isCajaLanding && isPopup && formDefinition.id === 'afeda06e-a058-48c9-b066-18662b9c9b11' && (
        <div className={classNames('lg:px-10 flex flex-col items-start gap-4 mb-12 font-montserrat')}>
          <div className="text-start font-montserrat font-semibold text-brand-soft-white uppercase">
            {cajaVillasHeadingData[router.locale].preHeading}
          </div>
          <div className="text-brand-soft-white font-montserrat text-[14px]">
            {cajaVillasHeadingData[router.locale].heading}
          </div>
        </div>
      )}
      {!submitted && isCajaLanding && isPopup && formDefinition.id === 'e50fff9b-c310-417d-8356-82ae3d6028a8' && (
        <div className={classNames('lg:px-10 flex flex-col items-start gap-4 mb-12 font-montserrat')}>
          <div className="text-start font-montserrat font-semibold text-brand-soft-white uppercase">
            {whatsappConsentHeadingData[router.locale].preHeading}
          </div>
          <div
            className="text-brand-soft-white font-montserrat text-[14px] [&>a]:italic [&>a]:font-bold"
            dangerouslySetInnerHTML={{ __html: whatsappConsentHeadingData[router.locale].heading }}
          ></div>
        </div>
      )}
      {(headingData.preHeading || headingData.errorHeading || headingData.heading || headingData.text) && (
        <div
          className={classNames(
            'section-container container flex flex-col items-center gap-4 mb-14 sm:mb-16 lg:mb-12 font-montserrat',
            dark ? 'text-brand-soft-white' : 'text-black',
          )}
        >
          {headingData.preHeading && (
            <div className="text-xs not-italic font-medium leading-normal tracking-quote-reg-mobile uppercase">
              {headingData.preHeading}
            </div>
          )}
          {headingData.heading && (
            <div className="heading sm:text-base sm:leading-5 sm:tracking-headline-m-tablet lg:text-xl lg:leading-6 lg:tracking-quote-reg-mobile not-italic font-semibold uppercase text-center">
              {headingData.heading}
            </div>
          )}

          {headingData.errorHeading && (
            <div className="errorHeading sm:text-base sm:leading-5 sm:tracking-headline-m-tablet lg:text-xl lg:leading-6 lg:tracking-quote-reg-mobile not-italic font-semibold uppercase text-center text-error">
              {headingData.errorHeading}
            </div>
          )}

          {headingData.text && (
            <div
              className="text text-sm not-italic font-normal leading-6 text-center max-w-[716px]"
              dangerouslySetInnerHTML={{ __html: headingData.text }}
            ></div>
          )}
          {headingData.cta && (
            <div className="mt-20">
              <Link className={classNames('btn link', { dark })} link={headingData.cta} />
            </div>
          )}
        </div>
      )}
      <div className={`container ${isPopup ? 'px-0' : ''}`}>
        {formDefinition && (
          <UmbracoForm
            className={classNames(
              'form-container last:[&_.newsletter-name-fieldset_.fieldset-column]:min-w-32 last:[&_.newsletter-name-fieldset_.fieldset-column]:w-auto',
              isPopup ? 'isPopup lg:px-8 xl:px-10 xxl:px-[88px]' : 'sm:px-8 lg:px-[88px]',
              !dark
                ? '[&_input]:!text-black [&_button[type="submit"]]:!text-black [&_input::placeholder]:!text-black/50 [&_label]:!text-black [&_button[type="submit"]]:!border-black/25 [&_input]:!border-black/25 ' +
                    '[&_textarea]:!text-black [&_textarea::placeholder]:!text-black/50 [&_textarea]:!border-black/25 ' +
                    '[&_.input-group.error_input]:!border-error [&_.checkbox_.checkmark]:border-black [&_.checkbox_.checkmark::after]:border-black [&_.input-group.error_label]:!text-error [&_input[type="submit"]:hover]:!border-brand-soft-white [&_button[type="submit"]:hover]:!border-brand-soft-white' +
                    '[&_.input.dropdown_button.main-button_.popover-title]:text-black/50 [&_.input.dropdown_button.main-button_.arrow_svg]:fill-black [&_.input.dropdown_button.main-button]:border-black/25 [&_.input.datepicker_button.main-button_.popover-title]:text-black/50 [&_.input.datepicker_button.main-button_.arrow_svg]:fill-black [&_.input.datepicker_button.main-button]:border-black/25'
                : '[&_.radio-mark]:after:bg-white [&_.radio-mark]:border-white [&_.radio-group]:text-white',
            )}
            formDefinition={formDefinition}
            setHeadingData={setHeadingData}
            onFinish={onFinish}
            preferedMethod={preferedMethod}
            setPreferedMethod={setPreferedMethod}
            submitted={submitted}
            setSubmitted={setSubmitted}
          />
        )}
      </div>
    </ModuleBase>
  );
};

export default FormModule;
